import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ThemeConfig {
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  accentForeground: string;
  ring: string;
  sidebarPrimary: string;
  sidebarRing: string;
  appName: string;
  logoUrl?: string;
  faviconUrl?: string;
  defaultLandingPage?: string;
}

const themes: Record<string, ThemeConfig> = {
  // GrowthHackers.com theme (production) - Teal/Green
  'growthhackers.com': {
    primary: '174 85% 32%',
    primaryForeground: '0 0% 100%',
    secondary: '174 41% 88%',
    secondaryForeground: '174 85% 15%',
    accent: '174 41% 88%',
    accentForeground: '174 85% 15%',
    ring: '174 85% 32%',
    sidebarPrimary: '174 85% 32%',
    sidebarRing: '174 85% 32%',
    appName: 'GrowthHackers',
    logoUrl: 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
  },
  // GrowthRocks.com theme - Vibrant Lime Green (based on uploaded image)
  'growthrocks.com': {
    primary: '88 85% 53%',
    primaryForeground: '0 0% 0%',
    secondary: '88 30% 88%',
    secondaryForeground: '88 85% 25%',
    accent: '88 30% 88%',
    accentForeground: '88 85% 25%',
    ring: '88 85% 53%',
    sidebarPrimary: '88 85% 53%',
    sidebarRing: '88 85% 53%',
    appName: 'GrowthRocks',
    logoUrl: 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
  },
  // OS.GrowthRocks.com theme - Same as GrowthRocks
  'os.growthrocks.com': {
    primary: '88 85% 53%',
    primaryForeground: '0 0% 0%',
    secondary: '88 30% 88%',
    secondaryForeground: '88 85% 25%',
    accent: '88 30% 88%',
    accentForeground: '88 85% 25%',
    ring: '88 85% 53%',
    sidebarPrimary: '88 85% 53%',
    sidebarRing: '88 85% 53%',
    appName: 'GrowthRocks',
    logoUrl: 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
  },
  // Supabase domain theme - Purple/Violet
  'hjodtxoylcvcwjgvuctk.supabase.co': {
    primary: '263 70% 50%',
    primaryForeground: '0 0% 100%',
    secondary: '263 20% 88%',
    secondaryForeground: '263 70% 25%',
    accent: '263 20% 88%',
    accentForeground: '263 70% 25%',
    ring: '263 70% 50%',
    sidebarPrimary: '263 70% 50%',
    sidebarRing: '263 70% 50%',
    appName: 'Growth OS',
    logoUrl: 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
  },
  // Localhost/development theme - Blue
  'localhost': {
    primary: '221 83% 53%',
    primaryForeground: '0 0% 100%',
    secondary: '221 20% 88%',
    secondaryForeground: '221 83% 25%',
    accent: '221 20% 88%',
    accentForeground: '221 83% 25%',
    ring: '221 83% 53%',
    sidebarPrimary: '221 83% 53%',
    sidebarRing: '221 83% 53%',
    appName: 'Growth OS',
    logoUrl: 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
  },
  '127.0.0.1': {
    primary: '221 83% 53%',
    primaryForeground: '0 0% 100%',
    secondary: '221 20% 88%',
    secondaryForeground: '221 83% 25%',
    accent: '221 20% 88%',
    accentForeground: '221 83% 25%',
    ring: '221 83% 53%',
    sidebarPrimary: '221 83% 53%',
    sidebarRing: '221 83% 53%',
    appName: 'Growth OS',
    logoUrl: 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
  },
};

export const useHostnameTheme = () => {
  // Initialize with default theme that includes the Supabase logo URL
  const [currentTheme, setCurrentTheme] = useState<ThemeConfig>({
    primary: '174 85% 32%',
    primaryForeground: '0 0% 100%',
    secondary: '174 41% 88%',
    secondaryForeground: '174 85% 15%',
    accent: '174 41% 88%',
    accentForeground: '174 85% 15%',
    ring: '174 85% 32%',
    sidebarPrimary: '174 85% 32%',
    sidebarRing: '174 85% 32%',
    appName: 'Growth OS',
    logoUrl: 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
  });
  const [isThemeLoading, setIsThemeLoading] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const fetchAndApplyTheme = async () => {
      const hostname = window.location.hostname;
      
      try {
        // Fetch hostname theme
        const { data: theme } = await supabase
          .from('hostname_themes')
          .select('*')
          .eq('hostname', hostname)
          .maybeSingle();

        // Build theme config from DB theme if available
        const themeConfig: ThemeConfig = theme ? {
          primary: theme.primary_color,
          primaryForeground: theme.primary_foreground,
          secondary: theme.secondary_color,
          secondaryForeground: theme.secondary_foreground,
          accent: theme.accent_color,
          accentForeground: theme.accent_foreground,
          ring: theme.ring_color,
          sidebarPrimary: theme.sidebar_primary,
          sidebarRing: theme.sidebar_ring,
          appName: theme.app_name,
          logoUrl: theme.logo_url && theme.logo_url.trim() !== ''
            ? theme.logo_url
            : 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
          faviconUrl: (theme as any).favicon_url && (theme as any).favicon_url.trim() !== ''
            ? (theme as any).favicon_url
            : undefined,
          defaultLandingPage: (theme as any).default_landing_page || undefined,
        } : {
          ...(themes[hostname] || {
            primary: '174 85% 32%',
            primaryForeground: '0 0% 100%',
            secondary: '174 41% 88%',
            secondaryForeground: '174 85% 15%',
            accent: '174 41% 88%',
            accentForeground: '174 85% 15%',
            ring: '174 85% 32%',
            sidebarPrimary: '174 85% 32%',
            sidebarRing: '174 85% 32%',
            appName: 'Growth OS',
            logoUrl: 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
          }),
          logoUrl: themes[hostname]?.logoUrl
            || 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
        };
        
        // Theme loaded silently
        
        // Apply theme to CSS variables
        const root = document.documentElement;
        root.style.setProperty('--primary', themeConfig.primary);
        root.style.setProperty('--primary-foreground', themeConfig.primaryForeground);
        root.style.setProperty('--secondary', themeConfig.secondary);
        root.style.setProperty('--secondary-foreground', themeConfig.secondaryForeground);
        root.style.setProperty('--accent', themeConfig.accent);
        root.style.setProperty('--accent-foreground', themeConfig.accentForeground);
        root.style.setProperty('--ring', themeConfig.ring);
        root.style.setProperty('--sidebar-primary', themeConfig.sidebarPrimary);
        root.style.setProperty('--sidebar-ring', themeConfig.sidebarRing);

        setCurrentTheme(themeConfig);
      } catch (error) {
        console.error('Error fetching theme:', error);
      } finally {
        setIsThemeLoading(false);
      }
    };

    fetchAndApplyTheme();
  }, []);

  return {
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
    hasCustomTheme: currentTheme !== null,
    appName: currentTheme.appName || 'Growth OS',
    logoUrl: currentTheme.logoUrl || 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/theme-assets/theme-logos/logo-1755684648969.png',
    faviconUrl: currentTheme.faviconUrl,
    defaultLandingPage: currentTheme.defaultLandingPage,
    theme: currentTheme,
    isThemeLoading,
  };
};